import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Container,
  useMediaQuery,
  useTheme,
  Badge,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ReimbursementV2Page from "./ReimbursementV2Page";
// import ReimbursementPageV1 from "./Reimbursement";
import InvoiceV2Page from "./InvoiceV2Page";
// import InvoiceV1Page from "./InvoiceV1Page";
// import HistoryIcon from "@mui/icons-material/History";
import ReceiptIcon from "@mui/icons-material/Receipt";
import reimbursementV2Api from "../services/reimbursement-v2-api";
import invoiceV2Api from "../services/invoice-v2-api";
import { useAuth } from "../contexts/AuthContext";
import { useBadgeRefresh } from "../contexts/BadgeRefreshContext";

/**
 * Combined Finance Desk Page with tabs for Reimbursements and Invoices
 * - Reimbursement V2 (New): New reimbursement request submission and management
 * - Reimbursement V1 (Historical): Read-only historical reimbursement requests
 * - Invoice V2 (New): New invoice request submission and management
 * - Invoice V1 (Historical): Read-only historical invoice requests
 */
const FinanceDesk: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);
  const { user } = useAuth();
  const { refreshTrigger } = useBadgeRefresh();
  const [reimbursementAttentionCount, setReimbursementAttentionCount] =
    useState(0);
  const [invoiceAttentionCount, setInvoiceAttentionCount] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Fetch attention counts for both reimbursement and invoice
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const fetchAttentionCounts = async () => {
      try {
        const [reimbursementCount, invoiceCount] = await Promise.all([
          reimbursementV2Api.getAttentionRequestsCount(user.id),
          invoiceV2Api.getAttentionRequestsCount(user.id),
        ]);
        setReimbursementAttentionCount(reimbursementCount);
        setInvoiceAttentionCount(invoiceCount);
      } catch (error) {
        console.error("Failed to fetch attention counts:", error);
      }
    };

    // Fetch immediately
    fetchAttentionCounts();

    // Refresh every 30 seconds
    const interval = setInterval(fetchAttentionCounts, 30000);

    return () => clearInterval(interval);
  }, [user?.id, refreshTrigger]);

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="finance desk tabs"
          indicatorColor="secondary"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
          }}
          variant="fullWidth"
        >
          <Tab
            label={t("reimbursement.v2.tab")}
            id="tab-reimbursement-v2"
            aria-controls="tabpanel-reimbursement-v2"
            icon={
              <Badge
                badgeContent={reimbursementAttentionCount}
                color="error"
                max={99}
              >
                <ReceiptIcon />
              </Badge>
            }
            iconPosition="top"
          />
          <Tab
            label={t("invoice.v2.tab")}
            id="tab-invoice-v2"
            aria-controls="tabpanel-invoice-v2"
            icon={
              <Badge
                badgeContent={invoiceAttentionCount}
                color="error"
                max={99}
              >
                <ReceiptIcon />
              </Badge>
            }
            iconPosition="top"
          />
        </Tabs>
      </Box>

      {/* Reimbursement V2 Tab Panel */}
      <Box
        role="tabpanel"
        hidden={activeTab !== 0}
        id="tabpanel-reimbursement-v2"
        aria-labelledby="tab-reimbursement-v2"
      >
        {activeTab === 0 && <ReimbursementV2Page />}
      </Box>

      {/* Invoice V2 Tab Panel */}
      <Box
        role="tabpanel"
        hidden={activeTab !== 1}
        id="tabpanel-invoice-v2"
        aria-labelledby="tab-invoice-v2"
      >
        {activeTab === 1 && <InvoiceV2Page />}
      </Box>
    </Container>
  );
};

export default FinanceDesk;
